/**
 * Templates — write once with merge fields, reuse across campaigns and
 * journey steps.
 */
import { useApp } from '../../store/AppContext.jsx';
import { looksHtml, htmlToText } from '../../components/modals/CampaignEditor.jsx';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, EmptyState } from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import { CHANNELS, MERGE, channelLabel } from '../../constants/marketing.js';
import { ChannelPill } from './MarketingBits.jsx';

/**
 * The first words of a template, for a list.
 *
 * An email campaign begins `<table role="presentation" cellpadding="0"...`, so
 * a straight slice of the body showed a column of identical markup and told
 * you nothing about which template was which.
 */
const excerpt = (body, n) => {
  const words = (looksHtml(body) ? htmlToText(body) : String(body || ''))
    .replace(/\s+/g, ' ')
    .trim();
  return words ? words.slice(0, n) + (words.length > n ? '…' : '') : '';
};

export default function Templates() {
  const { mkt, setSheet, removeMarketingRecord } = useApp();

  const perChannel = CHANNELS.map(([k]) => mkt.templates.filter(t => t.channel === k).length);

  const remove = (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this template?')) removeMarketingRecord('templates', id);
  };

  return (
    <>
      <TileRow
        tiles={[
          ['Templates', mkt.templates.length, 'file', 'g-orange'],
          ...CHANNELS.map(([k, label, icon, tone], i) => [label, perChannel[i], icon, tone]),
          ['Merge fields', MERGE.length, 'wand', 'g-pink']
        ]}
      />

      <Card>
        <CardHead icon="file" tone="g-orange" title="Templates" count={mkt.templates.length}>
          <button type="button" className="btn sm primary" onClick={() => setSheet({ kind: 'template' })}>
            <Icon name="plus" />New template
          </button>
        </CardHead>

        <div className="cbody">
          <p className="muted" style={{ marginBottom: 12 }}>
            Write once with merge fields and reuse across campaigns.{' '}
            {MERGE.map(m => <code className="mergechip" key={m[0]} title={m[1]}>{m[0]} </code>)}
          </p>

          {mkt.templates.length ? (
            <div className="tscroll">
              <table>
                <thead>
                  <tr><th>Template</th><th>Channel</th><th>Subject</th><th>Preview</th><th /></tr>
                </thead>
                <tbody>
                  {mkt.templates.map(t => (
                    <tr key={t.id} onClick={() => setSheet({ kind: 'template', id: t.id })}>
                      <td><b>{t.name}</b></td>
                      <td><ChannelPill channel={t.channel} /></td>
                      <td className="dim">{t.subject || '—'}</td>
                      <td className="dim" style={{ whiteSpace: 'normal', maxWidth: 340 }}>
                        {excerpt(t.body, 110) || '—'}
                      </td>
                      <td>
                        <div className="acts">
                          <button
                            type="button"
                            title="Edit"
                            onClick={e => { e.stopPropagation(); setSheet({ kind: 'template', id: t.id }); }}
                          >
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="rm" title="Delete" onClick={e => remove(e, t.id)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon="file" tone="g-orange" title="No templates yet">
              Write one with {channelLabel('email')} or {channelLabel('whatsapp')} and reuse it anywhere.
            </EmptyState>
          )}
        </div>
      </Card>
    </>
  );
}
